import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, lastValueFrom } from 'rxjs';
import * as Entities from '@shared/entities';
import { ListResponse, Result } from '@shared/models';
import { LoggerService } from '../services/logger.service';
import { CallCenterPhoneNumberImportRequest } from '@shared/entities/entities';
import { ConfigService } from '../services/config.service';

@Injectable({
  providedIn: 'root',
})
export class CallCenterPhoneNumbersApi {
  private serviceBase = '';

  constructor(
    private http: HttpClient,
    private log: LoggerService,
    private configService: ConfigService
  ) {
    this.configService.Config.subscribe((config) => {
      if (config?.apiUri) {
        this.serviceBase = `${config.apiUri}/callCenterPhoneNumbers/`;
      }
    });
  }

  async loadCallCenterPhoneNumbers(_force: boolean = false): Promise<ListResponse<Entities.CallCenterPhoneNumber>> {
    const result = await lastValueFrom(
      this.http.get<Result<ListResponse<Entities.CallCenterPhoneNumber>>>(this.serviceBase + 'list')
    );
    if (result.successful) {
      return result.data;
    }

    this.log.displayError(result.message || 'Unable to load call center phone numbers', 'Call Center Phone Number');
    return { records: [] } as ListResponse<Entities.CallCenterPhoneNumber>;
  }

  async import(request: CallCenterPhoneNumberImportRequest[]): Promise<Result<number>> {
    const result = await lastValueFrom(this.http.post<Result<number>>(this.serviceBase + 'import', request));
    if (result.successful) {
      if (result.errors.length > 0) {
        this.log.displayError(
          'Call Center Phone Number was successful but had the following errors',
          'Call Center Phone Number'
        );
      }
    } else {
      this.log.displayError('Call Center Phone Number Import Failed', 'Call Center Phone Number');
    }
    return result;
  }

  async loadCallCenterPhoneNumberTypes(
    _force: boolean = false
  ): Promise<ListResponse<Entities.CallCenterPhoneNumberType>> {
    const result = await lastValueFrom(
      this.http.get<Result<ListResponse<Entities.CallCenterPhoneNumberType>>>(this.serviceBase + 'list-types')
    );
    if (result.successful) {
      return result.data;
    }

    this.log.displayError(
      result.message || 'Unable to load call center phone number types',
      'Call Center Phone Number'
    );
    return { records: [] } as ListResponse<Entities.CallCenterPhoneNumberType>;
  }

  async create(callCenterPhoneNumber: Entities.CallCenterPhoneNumber): Promise<Result<number>> {
    console.debug('create', callCenterPhoneNumber);
    const result = await lastValueFrom(this.http.post<Result<number>>(this.serviceBase, callCenterPhoneNumber));
    if (!result.successful) {
      this.log.displayError('Call Center Phone Number Creation Failed', 'Call Center Phone Number');
    }
    return result;
  }

  async update(id: number, program: Entities.CallCenterPhoneNumber): Promise<Result<number>> {
    const result = await lastValueFrom(this.http.put<Result<number>>(this.serviceBase + id, program));
    if (result.successful) {
      this.log.displaySuccess('Call Center Phone Number Updated', 'Call Center Phone Number');
    } else {
      this.log.displayError('Call Center Phone Number Update Failed', 'Call Center Phone Number');
    }
    return result;
  }

  async delete(id: number): Promise<Result<number>> {
    const result = await lastValueFrom(this.http.delete<Result<number>>(this.serviceBase + id));
    if (!result.successful) {
      this.log.displayError('Call Center Phone Number Deletion Failed', 'Call Center Phone Number');
    }
    return result;
  }

  fetchAll(): Observable<Entities.CallCenterPhoneNumber[]> {
    return this.http.get<Entities.CallCenterPhoneNumber[]>(this.serviceBase);
  }

  fetchById(callCenterPhoneNumberId: number): Observable<Entities.CallCenterPhoneNumber> {
    return this.http.get<Entities.CallCenterPhoneNumber>(this.serviceBase + callCenterPhoneNumberId);
  }

  fetchPhoneNumberTypes(): Observable<Entities.CallCenterPhoneNumberType[]> {
    return this.http.get<Entities.CallCenterPhoneNumberType[]>(this.serviceBase + 'getPhoneNumberTypes');
  }

  // async allocate(callCenterPhoneNumber: Entities.CallCenterPhoneNumber): Promise<Result<number>> {
  //   var result = await lastValueFrom(this.http.put<Result<number>>(this.serviceBase + 'allocate/' + callCenterPhoneNumber.callCenterPhoneNumberId, JSON.stringify(callCenterPhoneNumber), { headers: { 'Content-Type': 'application/json' } }))
  //   if (result.successful) {
  //     return result;
  //   } else {
  //     this.log.displayError('Call Center Phone Number Allocation Failed', 'Call Center Phone Number');
  //   }
  // }

  // updateCallCenterPhoneNumber(callCenterPhoneNumber: Entities.CallCenterPhoneNumber): Observable<Entities.CallCenterPhoneNumber | string> {
  //   return this.http.put<Entities.CallCenterPhoneNumber>(this.serviceBase + callCenterPhoneNumber.callCenterPhoneNumberId, JSON.stringify(callCenterPhoneNumber), { headers: { 'Content-Type': 'application/json' } })
  //     .pipe(map((response: Entities.CallCenterPhoneNumber) => {
  //       return response;
  //     }), catchError(handleError<Entities.CallCenterPhoneNumber>('PutCallCenterPhoneNumber', null)));
  // }
}
